import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import HeroHeadline from "@/pages/home/components/HeroHeadline";
import HeroBadge from "@/pages/home/components/HeroBadge";
import { whatsappLink } from "@/constants/site";
import { trustItems } from "@/mocks/home";

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-foreground-950 text-background-50">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20navy%20technology%20background%20with%20glowing%20blue%20network%20mesh%20and%20subtle%20circuit%20lines%2C%20premium%20digital%20infrastructure%20visualization%2C%20minimal%20elegant&width=1600&height=900&seq=nh-hero-bg&orientation=landscape"
          alt="Fundo abstrato de tecnologia com rede de conexões representando infraestrutura digital e hospedagem de sites da NXHOST"
          title="NXHOST — hospedagem de sites, servidores e infraestrutura digital"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover object-top opacity-60"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/95 via-foreground-950/90 to-foreground-950/98"></div>
      <div className="absolute inset-0 nx-grid opacity-40"></div>
      <div className="nx-glow absolute -top-40 right-0 h-[520px] w-[520px] animate-pulseGlow"></div>
      <div className="nx-glow absolute bottom-0 -left-40 h-[420px] w-[420px] animate-pulseGlow"></div>

      <div className="relative mx-auto w-full max-w-7xl px-4 md:px-8 pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <HeroBadge />
            </Reveal>

            <Reveal delay={80}>
              <h1
                aria-label="Seu negócio merece uma infraestrutura digital à altura."
                className="relative mt-6 font-heading text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-[3.25rem]"
              >
                <span className="invisible" aria-hidden="true">
                  Seu negócio merece uma infraestrutura digital à altura.
                </span>
                <span className="absolute inset-0">
                  <HeroHeadline />
                </span>
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-background-200 md:text-lg">
                Hospedagem, servidores e <strong className="font-semibold text-background-50">Criação de sites de última geração</strong> para
                manter sua empresa online, estável e preparada para crescer.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
                <li className="flex items-center gap-2 text-sm font-medium text-background-100">
                  <i className="ri-rocket-2-line text-lg text-accent-300" aria-hidden="true"></i>
                  Sites de última geração
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-background-100">
                  <i className="ri-flashlight-line text-lg text-accent-300" aria-hidden="true"></i>
                  Carregamento rápido
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-background-100">
                  <i className="ri-search-eye-line text-lg text-secondary-400" aria-hidden="true"></i>
                  Indexados no Google (SEO)
                </li>
              </ul>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/solucoes"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  Conheça nossas soluções
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/20 bg-background-50/5 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-whatsapp-line text-lg text-secondary-400" aria-hidden="true"></i>
                  Falar no WhatsApp
                </a>
              </div>
            </Reveal>

            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
                <div>
                  <p className="font-heading text-2xl font-bold text-background-50">+19 anos</p>
                  <p className="text-xs text-background-300">de experiência</p>
                </div>
                <span className="hidden h-10 w-px bg-background-50/15 sm:block"></span>
                <div>
                  <p className="font-heading text-2xl font-bold text-background-50">100%</p>
                  <p className="text-xs text-background-300">foco em empresas</p>
                </div>
                <span className="hidden h-10 w-px bg-background-50/15 sm:block"></span>
                <div>
                  <p className="font-heading text-2xl font-bold text-background-50">BR</p>
                  <p className="text-xs text-background-300">servidores no Brasil</p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={200}>
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              <div className="overflow-hidden rounded-2xl border border-background-50/10 bg-foreground-900">
                <img
                  src="https://storage.helloreaddy.io/project_files/9d609ae5-b03e-4dcf-a75d-08eaee046d5d/ac6aa788-2b35-4317-a350-0759bc8c4265_compressed_criacao-sites.webp"
                  alt="Painel de hospedagem de sites e dashboard de performance exibido em um notebook"
                  title="Painel de hospedagem NXHOST — performance, disponibilidade e suporte"
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="absolute -left-3 top-8 hidden w-44 rounded-xl border border-background-50/10 bg-foreground-950/90 p-4 backdrop-blur-sm sm:block animate-floaty">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-500/15 text-secondary-400">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-[11px] text-background-300">Disponibilidade</p>
                    <p className="text-sm font-semibold text-background-50">Alta</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-3 bottom-10 hidden w-44 rounded-xl border border-background-50/10 bg-foreground-950/90 p-4 backdrop-blur-sm sm:block animate-floatySlow">
                <p className="text-[11px] text-background-300">Performance</p>
                <p className="font-heading text-xl font-bold text-accent-400">99%</p>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-50/10">
                  <div className="h-full w-[92%] rounded-full bg-gradient-to-r from-primary-500 to-accent-400"></div>
                </div>
              </div>

              <div className="absolute -top-4 right-6 hidden h-12 w-12 items-center justify-center rounded-xl border border-background-50/10 bg-foreground-900/80 text-primary-300 backdrop-blur-sm md:flex animate-floaty">
                <i className="ri-cloud-line text-2xl" aria-hidden="true"></i>
              </div>
              <div className="absolute -bottom-3 left-10 hidden h-12 w-12 items-center justify-center rounded-xl border border-background-50/10 bg-foreground-900/80 text-accent-300 backdrop-blur-sm md:flex animate-floatySlow">
                <i className="ri-server-line text-2xl" aria-hidden="true"></i>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="relative border-t border-background-50/10 bg-foreground-950/60">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-6 px-4 py-7 md:grid-cols-4 md:px-8">
          {trustItems.map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background-50/5 text-accent-300">
                <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
              </span>
              <span className="text-xs font-medium leading-snug text-background-200 md:text-sm">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}